import { formatCount, formatPercent, tt } from '@agos/shared'

/**
 * `/settings-notifications` — which channels carry each event, and in what order.
 *
 * **FB-144.** Two of the nine events are configured with **push alone**, and push reaches **104 of the
 * 2 464 customers** in the restored data — 114 subscriptions across 104 people, 4,2 %. `order_delivered`
 * and `points_granted` are therefore delivered to one customer in twenty-four and to nobody else, while the
 * page shows *1/1 aktívnych* and a green chip. Legacy's own info box promises the opposite:
 *
 * > Push kanál sa pošle len zákazníkom, ktorí majú zariadenie prihlásené… Ak ho nemajú a push je primárny
 * > kanál v fallback režime, systém automaticky padne na email alebo SMS.
 *
 * There is nothing to fall back to when push is the only channel enabled. The promise is in the paragraph
 * directly above the toggles that make it impossible.
 *
 * **FB-145.** Every click is a save, and a refused one stays on screen:
 *
 * ```tsx
 * setEvents(next)                       // optimistic
 * const res = await fetch(…)            // PUT
 * if (!res.ok) throw new Error()
 * } catch { toast({ title: "Chyba ukladania" }) }   // …and `next` is still there
 * ```
 *
 * So a 403 leaves the chip green, the screen and the server disagreeing, and the only trace a toast that
 * scrolls away. This is FB-124's and FB-133's family: a local copy presented as the truth.
 *
 * **The §10A.4 surface this page does not know about.** `NotificationDispatcher` has three strategies where
 * the page offers two, a per-channel **master switch** that overrides every row here, a degraded-channel
 * state, an explicit channel order, and a delivery log. A channel switched off centrally makes every toggle
 * on this page a lie, so the master state is read and shown. The rest is a §9.5 build, recorded as owed.
 */

export type Channel = 'push' | 'email' | 'sms'

/** What the dispatcher does by default, cheapest first — its own `DEFAULT_ORDER`. */
export const DEFAULT_ORDER: readonly Channel[] = ['push', 'sms', 'email']

/** The order legacy's info box claims, which is not the dispatcher's. */
export const LEGACY_CLAIMED_ORDER: readonly Channel[] = ['push', 'email', 'sms']

export type Mode = 'fallback' | 'all'

export interface EventChannels {
  key: string
  label: string
  desc: string
  category: string
  email: boolean
  sms: boolean
  push: boolean
  mode: Mode
  updatedAt: string | null
}

export interface ChannelMaster {
  channel: Channel
  isEnabled: boolean
  isDegraded: boolean
  consecutiveFailures: number
}

export function eventsOf(payload: unknown): EventChannels[] {
  const events = (payload as { events?: unknown })?.events
  if (!Array.isArray(events)) return []
  return events.map((entry) => {
    const row = entry as Record<string, unknown>
    return {
      key: typeof row.key === 'string' ? row.key : '',
      label: typeof row.label === 'string' ? row.label : '',
      desc: typeof row.desc === 'string' ? row.desc : '',
      category: typeof row.category === 'string' ? row.category : '',
      email: row.email === true,
      sms: row.sms === true,
      push: row.push === true,
      mode: row.mode === 'all' ? 'all' : 'fallback',
      updatedAt: typeof row.updatedAt === 'string' ? row.updatedAt : null,
    }
  })
}

export function mastersOf(payload: unknown): ChannelMaster[] {
  if (!Array.isArray(payload)) return []
  return payload.flatMap((entry) => {
    const row = entry as Record<string, unknown>
    const channel = row.channel
    if (channel !== 'push' && channel !== 'email' && channel !== 'sms') return []
    return [
      {
        channel,
        isEnabled: row.isEnabled === true,
        isDegraded: row.isDegraded === true,
        consecutiveFailures: typeof row.consecutiveFailures === 'number' ? row.consecutiveFailures : 0,
      },
    ]
  })
}

/** Categories in the order the server returns them, each keeping its own event order. */
export function grouped(
  events: readonly EventChannels[],
): { category: string; events: EventChannels[] }[] {
  const order: string[] = []
  const byCategory = new Map<string, EventChannels[]>()
  for (const event of events) {
    if (!byCategory.has(event.category)) {
      byCategory.set(event.category, [])
      order.push(event.category)
    }
    byCategory.get(event.category)?.push(event)
  }
  return order.map((category) => ({ category, events: byCategory.get(category) ?? [] }))
}

export function enabledChannels(event: EventChannels): Channel[] {
  return DEFAULT_ORDER.filter((channel) => event[channel])
}

// ─── FB-144: who the event actually reaches ───────────────────────────────────

/**
 * How many customers push can actually reach.
 *
 * `customer_push_subscriptions` holds 114 rows across 104 of the 2 462 customers on the restored data —
 * **4,2 %**. The only code that counted them used to sit behind `POST /customers/bulk-push`, a send
 * path this repository may not call (`BLOCKED.md`), so the page was handed `null` and said the
 * qualitative thing instead of a figure it could not source — hard-coding the measured 4,2 % would have
 * been FB-137's defect one directory over.
 *
 * **`GET /notifications/channels/coverage` answers it now** (`MIGRATION-STATUS` FB-144, 2026-08-02),
 * read-only, so the page states the real number. `null` is still handled: a request in flight, or a
 * server that does not carry the endpoint, must not print a percentage of nothing.
 */
export interface PushCoverage {
  subscribers: number
  customers: number
}

export function coverageRatio(coverage: PushCoverage | null): number | null {
  if (coverage === null || coverage.customers <= 0) return null
  return (coverage.subscribers / coverage.customers) * 100
}

export type Reach = 'everyone' | 'push-only' | 'nobody'

/**
 * Who an event's configuration can reach.
 *
 * **push-only** is the one worth naming: push needs a subscription the customer created on the website, and
 * a customer without one has no other channel to receive it on. Every other channel reaches anybody whose
 * record has the address or the number.
 */
export function reachOf(event: EventChannels): Reach {
  const channels = enabledChannels(event)
  if (channels.length === 0) return 'nobody'
  if (channels.length === 1 && channels[0] === 'push') return 'push-only'
  return 'everyone'
}

export function reachMessage(reach: Reach, coverage: PushCoverage | null = null): string {
  if (reach === 'nobody') {
    return tt('Žiadny kanál nie je zapnutý, takže toto oznámenie nedostane nikto.')
  }
  if (reach !== 'push-only') return ''

  const only = tt(
    'Push dostane len zákazník, ktorý si prihlásil prehliadač. Ostatní toto oznámenie nedostanú - pri jedinom zapnutom kanáli nie je na čo prepnúť.',
  )
  const ratio = coverageRatio(coverage)
  if (coverage === null || ratio === null) {
    // In flight, or an endpoint that is not there. A made-up percentage is worse than none.
    return `${only} ${tt('Koľko zákazníkov to je, server práve nehlási.')}`
  }
  return `${tt('Push má prihlásených')} ${formatCount(coverage.subscribers)} ${tt('z')} ${formatCount(coverage.customers)} ${tt('zákazníkov')} (${formatPercent(ratio, 1)}). ${only}`
}

/** Whether the page should say something about this event's reach. */
export function reachNeedsSaying(event: EventChannels): boolean {
  return reachOf(event) !== 'everyone'
}

// ─── The master switches ──────────────────────────────────────────────────────

export type MasterState = 'on' | 'off' | 'degraded'

export function masterState(masters: readonly ChannelMaster[], channel: Channel): MasterState {
  const master = masters.find((entry) => entry.channel === channel)
  if (master === undefined) return 'on'
  if (!master.isEnabled) return 'off'
  return master.isDegraded ? 'degraded' : 'on'
}

/**
 * A channel switched off centrally overrides every row on this page.
 *
 * So a green chip for a channel whose master is off is exactly the kind of statement this port exists to
 * stop making.
 */
export function overriddenChannels(event: EventChannels, masters: readonly ChannelMaster[]): Channel[] {
  return enabledChannels(event).filter((channel) => masterState(masters, channel) === 'off')
}

export function masterMessage(state: MasterState, channel: Channel): string {
  const name = channelLabel(channel)
  if (state === 'off') {
    return `${name}: ${tt('kanál je centrálne vypnutý, takže nič neodíde, hoci je tu zapnutý.')}`
  }
  if (state === 'degraded') {
    return `${name}: ${tt('kanál je označený ako nefunkčný po opakovaných chybách.')}`
  }
  return ''
}

export function channelLabel(channel: Channel): string {
  if (channel === 'push') return 'Push'
  if (channel === 'email') return tt('E-mail')
  return 'SMS'
}

export function modeLabel(mode: Mode): string {
  return mode === 'all' ? tt('Všetky kanály') : tt('Prvý, ktorý doručí')
}

/**
 * What the mode really does.
 *
 * The dispatcher's own docblock is blunt about it: legacy's `fallback` reacts to **success**, so a channel
 * that *fails* falls through to nothing at all. The third strategy — walk the order and step over failures —
 * exists in the backend and has no control on this screen.
 */
export function modeMessage(mode: Mode): string {
  if (mode === 'all') {
    return tt('Odíde každým zapnutým kanálom naraz. Pre kritické oznámenia - a platí sa za každý.')
  }
  return tt(
    'Odíde najlacnejším zapnutým kanálom a ďalšie sa preskočia, keď prvý doručí. Keď prvý zlyhá, ďalší sa neskúsi.',
  )
}

/** The order the dispatcher walks, which is not the order legacy's info box claims. */
export function orderMessage(): string {
  return `${tt('Poradie kanálov')}: ${DEFAULT_ORDER.map((channel) => channelLabel(channel)).join(' → ')}`
}

export function activeCount(events: readonly EventChannels[]): number {
  return events.filter((event) => enabledChannels(event).length > 0).length
}

/** Only the four keys the endpoint accepts, so a save cannot carry a label or a timestamp back. */
export function payloadOf(event: EventChannels): Record<string, unknown> {
  return { email: event.email, sms: event.sms, push: event.push, mode: event.mode }
}

/** Turning the last channel off is a decision, not a typo — worth confirming rather than refusing. */
export function wouldSilence(event: EventChannels, channel: Channel, next: boolean): boolean {
  const after = { ...event, [channel]: next }
  return enabledChannels(after).length === 0
}
